import { createAsyncThunk } from '@reduxjs/toolkit'
import toCamel from 'lodash-humps'
import {
  createFetchAsyncThunk,
  csrfToken,
  toSnake,
  throwErrorForErrorResponse,
  throwTextErrorForErrorResponse,
  errorHandler,
  ErrorPayload
} from '../common'
import { closeModal } from './subLayoutSlice'
import { setLayoutableLockVersion } from '../article'
import type { SubLayoutState } from './types'

type ThunkConfig = {
  state: { common: { baseUri: string }; subLayout: SubLayoutState }
  rejectValue: ErrorPayload
}

type CreateSubLayoutParams = {
  navigate: (path: string) => void
  formData: FormData
}

type ModifySubLayoutParams = {
  layoutableType?: string
  layoutableId: number | string
  formData: FormData
}

type SubLayoutIdParams = {
  id: number | string
} & Record<string, unknown>

type CopySubLayoutParams = {
  id: number | string
  navigate: (path: string) => void
} & Record<string, unknown>

export const fetchSubLayouts = createFetchAsyncThunk('subLayout/fetchPages', 'sub_layouts')

export const createSubLayout = createAsyncThunk<unknown, CreateSubLayoutParams, ThunkConfig>(
  'subLayout/createSubLayout',
  async ({ navigate, formData }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}sub_layouts`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then(async (response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { subLayoutId } = payload as { subLayoutId: string }
        dispatch(closeModal())
        navigate(`${baseUri}sub_layouts/${subLayoutId}/articles`)
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)

export const modifySubLayout = createAsyncThunk<unknown, ModifySubLayoutParams, ThunkConfig>(
  'subLayout/modifySubLayout',
  async ({ layoutableId, formData }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}sub_layouts/${layoutableId}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().subLayout
        dispatch(fetchSubLayouts({ page: currentPage, sort }))
        dispatch(closeModal())
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const deleteSubLayout = createAsyncThunk<unknown, SubLayoutIdParams, ThunkConfig>(
  'subLayout/deleteSubLayout',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}sub_layouts/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().subLayout
        dispatch(fetchSubLayouts({ page: currentPage, sort }))
        dispatch(closeModal())
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const fetchLayoutResult = createAsyncThunk<unknown, { id: number | string }, ThunkConfig>(
  'subLayout/layoutResult',
  async ({ id }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}sub_layouts/${id}/layout_result`, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(throwTextErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const exportLayout = createAsyncThunk<unknown, { id: number | string; fileName: string }, ThunkConfig>(
  'subLayout/exportLayout',
  async ({ id }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}sub_layouts/${id}/export_layout.zip`)
      .then(throwErrorForErrorResponse)
      .then((response) => response.blob())
      .catch(errorHandler(rejectWithValue))
  }
)

export const copySubLayout = createAsyncThunk<unknown, CopySubLayoutParams, ThunkConfig>(
  'subLayout/copySubLayout',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { navigate, ...bodyParams } = params

    return fetch(`${baseUri}sub_layouts/${bodyParams.id}/copy`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(bodyParams))
    })
      .then(throwErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { subLayoutId } = payload as { subLayoutId: string }
        dispatch(closeModal())
        navigate(`${baseUri}sub_layouts/${subLayoutId}/articles?copiedSuccessfully=true`)
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)

export const modifyLayoutIndex = createAsyncThunk<unknown, SubLayoutIdParams, ThunkConfig>(
  'subLayout/modifyLayoutIndex',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}sub_layouts/${id}/layout_index`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwTextErrorForErrorResponse)
      .then(async (response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { lockVersion } = payload as { lockVersion: number }
        dispatch(setLayoutableLockVersion(lockVersion))
        return payload
      })
      .catch(errorHandler(rejectWithValue))
  }
)
